import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { defaultTenantConfig, mergeTenantConfig, TenantConfig, TenantProfileOverrides } from '../config/tenant';
import { db } from '../services/firebase';
import { tenantProfileDocRef } from '../services/firestorePaths';
import { useAuth } from '../hooks/useAuth';
import { resolveTenantRoutingContext } from '../utils/tenantRouting';

interface TenantContextType {
  tenant: TenantConfig;
}

const TenantContext = createContext<TenantContextType>({
  tenant: defaultTenantConfig,
});

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<TenantConfig>(defaultTenantConfig);
  const [routingTenantSlug, setRoutingTenantSlug] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const routing = resolveTenantRoutingContext({
      hostname: window.location.hostname,
      pathname: window.location.pathname,
      baseDomain: 'aspis.software',
    });

    setRoutingTenantSlug(routing.tenantSlug);
  }, []);

  useEffect(() => {
    if (!user) {
      setTenant(defaultTenantConfig);
      return;
    }

    const resolvedTenantId = user?.tenantId || routingTenantSlug;

    if (!resolvedTenantId) {
      setTenant(defaultTenantConfig);
      return;
    }

    const tenantId = resolvedTenantId;
    const profileRef = tenantProfileDocRef(db, tenantId);

    const unsub = onSnapshot(
      profileRef,
      (snap) => {
        const overrides = snap.exists() ? (snap.data() as TenantProfileOverrides) : null;
        const merged = mergeTenantConfig(
          {
            ...defaultTenantConfig,
            tenantId,
            displayName: user?.tenantName || defaultTenantConfig.displayName,
          },
          overrides
        );
        setTenant(merged);
      },
      (err) => {
        console.error('Tenant profile sync failed', err);
        setTenant({
          ...defaultTenantConfig,
          tenantId,
          displayName: user?.tenantName || defaultTenantConfig.displayName,
        });
      }
    );

    return () => unsub();
  }, [routingTenantSlug, user?.tenantId, user?.tenantName]);

  const value = useMemo(() => ({ tenant }), [tenant]);
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  return useContext(TenantContext);
};
